import React, {
  useContext,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Context } from "../context/Context";
import AddRoom from "./AddRoom";
import { useNavigate, useParams } from "react-router-dom";
import { DeleteRoom, UpdateRoom } from "../api/api";
import WarningAlert from "./sweeAlerts/WarningAlert";
import SuccessToast from "./toasts/SuccessToast";
import ErrorToast from "./toasts/ErrorToast";

const RoomsList = () => {
  const {
    rooms,
    setRooms,
    currentRoom,
    setCurrentRoom,
    authUser,
    equipments,
    setRoomEquipments,
    setIsLoading,
    setSelectedEquipment,
  } = useContext(Context);

  const navigate = useNavigate();
  const { room_id: roomId } = useParams();
  const [editRoom, setEditRoom] = useState(null);
  const [editedRoomName, setEditedRoomName] = useState("");

  // استفاده از ref برای جلوگیری از رندر مجدد
  const isSelectingRef = useRef(false);
  const lastSelectedRoomRef = useRef(null);

  const handleEquipmentNavigation = useCallback(
    (equipment, roomIdParam) => {
      const eq = equipment.equipment || equipment;
      const type = eq.type;
      const equipmentId = eq.id;

      setSelectedEquipment({
        id: equipmentId,
        type: type,
      });

      const routes = {
        1: `/room/key/${roomIdParam}/${equipmentId}/${type}`,
        3: `/room/remote/${roomIdParam}/${equipmentId}/${type}`,
        4: `/room/dimmer/${roomIdParam}/${equipmentId}/${type}`,
        5: `/room/thermostat/${roomIdParam}/${equipmentId}/${type}`,
        9: `/room/rgb/${roomIdParam}/${equipmentId}/${type}`,
        99: `/room/wireless-key/${roomIdParam}/${equipmentId}/${type}`,
        100: `/room/television/${roomIdParam}/${equipmentId}/${type}`,
        401: `/room/scenario-key/${roomIdParam}/${equipmentId}/${type}`,
        501: `/room/fan-coil/${roomIdParam}/${equipmentId}/${type}`,
        502: `/room/motor/${roomIdParam}/${equipmentId}/${type}`,
        503: `/room/humidity/${roomIdParam}/${equipmentId}/${type}`,
        600: `/room/data-logger/${roomIdParam}/${equipmentId}/${type}`,
        9998: `/room/input/${roomIdParam}/${equipmentId}/${type}`,
      };

      const route = routes[type] || `/room/${roomIdParam}`;
      navigate(route);
    },
    [navigate, setSelectedEquipment],
  );

  const handleSelectRoom = useCallback(
    (room_id) => {
      // جلوگیری از انتخاب مکرر همان اتاق
      // if (!room_id || room_id == lastSelectedRoomRef.current || isSelectingRef.current) {
      //   return;
      // }

      const currentActiveId = roomId ? parseInt(roomId) : currentRoom;
      if (room_id == currentActiveId) return;

      isSelectingRef.current = true;
      lastSelectedRoomRef.current = room_id;

      setCurrentRoom(room_id);

      const roomEquipmentsFiltered = equipments.filter(
        (eq) => eq.room_id == room_id,
      );
      setRoomEquipments(roomEquipmentsFiltered);

      const equipment = roomEquipmentsFiltered[0];

      if (equipment) {
        handleEquipmentNavigation(equipment, room_id);
      } else {
        navigate(`/room/${room_id}`);
      }

      // ریست ref بعد از یه مدت کوتاه
      setTimeout(() => {
        isSelectingRef.current = false;
      }, 100);
    },
    [
      currentRoom,
      roomId,
      equipments,
      navigate,
      setCurrentRoom,
      setRoomEquipments,
      handleEquipmentNavigation,
    ],
  );

  const handleRemoveRoom = useCallback(
    (roomIdToRemove) => {
      WarningAlert(
        "از حذف این اتاق اطمینان دارید؟",
        "",
        "تایید",
        "انصراف",
      ).then((result) => {
        if (result.value) {
          setIsLoading(true);
          DeleteRoom({ room_id: roomIdToRemove })
            .then(() => {
              const newRooms = rooms.filter((r) => r.id !== roomIdToRemove);
              setRooms(newRooms);
              setIsLoading(false);
              SuccessToast("اتاق با موفقیت حذف شد");

              const currentActiveId = roomId ? parseInt(roomId) : currentRoom;
              if (roomIdToRemove == currentActiveId && newRooms[0]) {
                handleSelectRoom(newRooms[0].id);
              }
            })
            .catch(() => {
              setIsLoading(false);
              ErrorToast("خطا در حذف اتاق");
            });
        }
      });
    },
    [rooms, setRooms, setIsLoading, currentRoom, roomId, handleSelectRoom],
  );

  const handleEditSubmit = useCallback(
    (roomIdToEdit) => {
      if (!editedRoomName.trim()) {
        return ErrorToast("نام اتاق را وارد کنید");
      }

      setIsLoading(true);
      UpdateRoom({ name: editedRoomName.trim(), room_id: roomIdToEdit })
        .then(() => {
          setRooms((prev) =>
            prev.map((room) =>
              room.id == roomIdToEdit
                ? { ...room, name: editedRoomName.trim() }
                : room,
            ),
          );
          SuccessToast("اتاق با موفقیت ویرایش شد");
          setEditRoom(null);
          setEditedRoomName("");
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
          ErrorToast("خطا در ویرایش اتاق");
        });
    },
    [editedRoomName, setRooms, setIsLoading],
  );

  const activeRoomId = roomId ? parseInt(roomId) : currentRoom;

  // فقط در صورتی که rooms تغییر کرده باشد، رندر کن
  const roomsHash = rooms.map((r) => `${r.id}-${r.name}`).join(",");

  return (
    <>
      <div className="rooms-list d-none d-lg-block">
        <ul className="list-group">
          {rooms.map((room) => {
            const isActive = room.id == activeRoomId;
            const isEditing = editRoom == room.id;

            return (
              <li
                className="list-group-item"
                key={room.id}
                onClick={(e) => {
                  e.preventDefault();
                  handleSelectRoom(room.id);
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  {!isEditing ? (
                    <>
                      <a
                        className={isActive ? "active" : ""}
                        style={{ cursor: "pointer", textDecoration: "none" }}
                      >
                        {room.name}
                      </a>
                      {authUser?.is_admin && (
                        <div className="d-flex gap-1">
                          <div
                            className="edit-room"
                            onClick={() => {
                              setEditRoom(room.id);
                              setEditedRoomName(room.name);
                            }}
                          >
                            <i className="bx bx-edit"></i>
                          </div>
                          <div
                            className="remove-room"
                            onClick={() => handleRemoveRoom(room.id)}
                          >
                            <i className="bx bx-trash"></i>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-100">
                      <div className="mb-2">
                        <input
                          className="form-control form-control-sm"
                          type="text"
                          placeholder="نام اتاق را وارد کنید"
                          value={editedRoomName}
                          onChange={(e) => setEditedRoomName(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-xs btn-success-app"
                          onClick={() => handleEditSubmit(room.id)}
                        >
                          ثبت
                        </button>
                        <button
                          type="button"
                          className="btn btn-xs btn-outline-secondary"
                          onClick={() => {
                            setEditRoom(null);
                            setEditedRoomName("");
                          }}
                        >
                          انصراف
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
          {authUser?.is_admin && <AddRoom rooms={rooms} setRooms={setRooms} />}
        </ul>
      </div>
      <ul className="menu-inner d-flex flex-row overflow-auto d-lg-none">
        {authUser?.is_admin && <AddRoom rooms={rooms} setRooms={setRooms} />}
        {rooms.map((room) => {
          const isActive = room.id == activeRoomId;
          return (
            <li
              className={`menu-item ${isActive ? "active" : ""}`}
              key={room.id}
            >
              <a
                className="menu-link"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.preventDefault();
                  handleSelectRoom(room.id);
                }}
              >
                {room.name}
              </a>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default RoomsList;
